import APIClient, { failSafe, makeCancellableAPIRequest } from './index';
import _ from 'lodash';
import moment from 'moment';
import { getFindingAPIDurationFilter, getStartAndEndDateForQuickOption } from 'utils/dateUtils';

const UserAPI = {
    getAll(productId, accessLevel) {
        const params = {};
        if (productId) params.productId = productId;
        // accessLevel possible values undefined,TEAM,TENANT
        if (accessLevel) params.accessLevel = accessLevel;
       
        return makeCancellableAPIRequest(
            'GET',
            '/user/get-users',
            { params }
        );
    },
    getUsers(data) {
        const { filters, ...restParams } = data;
        const queryParams = new URLSearchParams(_.omitBy(restParams, _.isNil));           
        if (filters.disableLogin) {
            const isEnabled = filters.disableLogin.includes('Enabled');
            filters.disableLogin = 
                filters.disableLogin.length === 1 ? [!isEnabled] :
                filters.disableLogin.length === 2 ? [true, false] :
                undefined;
        }
        if (!data.sort || !data.sortColumn) delete data.sort;
        if (!data.sortOrder) delete data.sortOrder;  
        if(filters?.lastlogin && filters.lastlogin.length === 1){
            filters.lastLogin = getStartAndEndDateForQuickOption({
                label: filters.lastlogin[0],
                utcString: true,
            });
            delete filters.lastlogin;
        } else {
            filters.lastLogin = filters.lastlogin;
            delete filters.lastlogin;
        }
        
        return makeCancellableAPIRequest(
            'POST',
            `/api/v2/user/search?${queryParams.toString()}`,
            {
                data: filters
            },
        )
    },   
    validate(email={}) {
        return makeCancellableAPIRequest(
            'POST',
            '/api/v2/user/email/availability',
            {
                data: email
            },
            false
       )
    },    
    getUsersFilters(){
        return makeCancellableAPIRequest(
            'GET',
            '/api/v2/user/filters'
        )
    },
    getUserEnabledDisabled() {
        return makeCancellableAPIRequest(
            'GET',
            '/api/user/count'
        )
    },
    addUser(data){
        return makeCancellableAPIRequest(
            'POST',
            '/user/add/user',
            {
                data: data
            }
        )
    },
    updateUser(data){
        return makeCancellableAPIRequest(
            'PUT',
            '/user/update/user',
            {
                data: data
            }
        )
    },
    resetPassword(data){
        return makeCancellableAPIRequest(
            'POST',
            '/user/reset/password',
            {
                data: data
            }
        )
    },
    deleteUser(userId){
        return makeCancellableAPIRequest(
            'DELETE',
            `/user/data/remove/${userId}`
        )
    },
    bulkUserOperation(data){
        return makeCancellableAPIRequest(
            'POST',
            '/api/v2/user/bulk',
            {
                data: data
            }
        )
    },
    updateTenant(tenantId, businessUnitId){
        return makeCancellableAPIRequest(
            'GET',
            `/user/change/account/${tenantId}/${businessUnitId}`
        )
    },
    getRoles(){
        return makeCancellableAPIRequest(
            'GET',
            '/user/roles',
        )
    },
    getAssignableRolesForBU(id, attr='businessUnitId'){
        return makeCancellableAPIRequest(
            'GET',
            '/user/assignable/roles',
            {
                params: {
                    [attr]: id
                },
            }
        )
    },
    getAssignableRoles(params){
        return makeCancellableAPIRequest(
            'GET',
            '/user/assignable/roles',
            { params }
        )
    },
    getCurrentUserRole(){
        return makeCancellableAPIRequest(
            'GET',
            '/user/details/role'
        )
    },
    getBusinessUnit(){
        return makeCancellableAPIRequest(
            'GET',
            '/user/business-units/'
        )
    },
    getCurrentSession(){
        return makeCancellableAPIRequest(
            'GET',
            '/user/details/session'
        )
    },
    updateCurrentSession(){
        return makeCancellableAPIRequest(
            'GET',
            ' /user/update/session-time'
        )
    },
    switchBusinessUnit(tenantId, businessUnitId, params={}){
        return makeCancellableAPIRequest(
            'GET',
            `/user/change/account/${tenantId}/${businessUnitId}`,
            { params }
        )
    },
    getAuditLog(data){
        let newFilter = data.filters??{};
        if (newFilter.selectedDuration) {
            newFilter = getFindingAPIDurationFilter(newFilter, newFilter.selectedDuration)
        }
        const { foundOn: createdAt } = newFilter;
        const dates = createdAt || newFilter.createdAt;
        const params = {
            ...newFilter,
            startDate: dates && moment(dates?.[0]).valueOf(),
            endDate: dates && moment(dates?.[1]).valueOf(),
            ..._.omit(data, ['filters', 'sortColumn', 'sortOrder'])
        }
        return makeCancellableAPIRequest(
            'GET',
            '/user/audit/log/details/page',
            { params }
        )
    },
    getAuditLogFilters(){
        return makeCancellableAPIRequest(
            'GET',
            '/user/audit/log/filters',
        )
    },
    getByEmail(email){
        return makeCancellableAPIRequest(
            'GET',
            `/user/basicdetails/${email}`,
            undefined,
            false
        )
    },
    getTopProductPreference(BUaggregation = false){
        const path = BUaggregation ? '/api/businessunits/dashboard/preference/topProductList': '/user/preference/topProductList'
        return makeCancellableAPIRequest(
            'GET',
            path
        )
    },
    updateTopProductPreference(data){
        return makeCancellableAPIRequest(
            'POST',
            '/user/preference/topProductList',
            {
                data
            }
        )
    },
    setDefaultAccount(data){
        return makeCancellableAPIRequest(
            'POST',
            '/user/preference/defaultTenant',
            {
                data
            }
         )
    },
    getDefaultAccount(){
        return makeCancellableAPIRequest(
            'GET',
            '/user/preference/defaultTenant'
         )
    },
    getDefaultBU(tenantId){
        return makeCancellableAPIRequest(
            'GET',
            '/user/preference/defaultBU',
            {
                params: {
                    tenantId
                }
            }
         )
    },
    setDefaultBU(buId, userId){
        return failSafe(
            APIClient.post('/user/preference/defaultBU', {
                buId, userId
            })
        )
    },
    getAccessTeams(params, showDefaultMessage = false){
        return makeCancellableAPIRequest(
            'GET',
            `/public/get-teams?uuid=${params.uuid}`,
            showDefaultMessage
        )
    },
    seekAccess(data, showDefaultMessage = false){
        return failSafe(
            APIClient.put('/public/seekAccess', data, showDefaultMessage)
        );
    },
    oktaLogin(email, showDefaultMessage = false){
        return failSafe(
            APIClient.get(`/public/oidc/login?email=${email}`), showDefaultMessage
        );
    },
    // simplifyOktaLogin(email, showDefaultMessage = false){
    //     return failSafe(
    //         APIClient.get(`/public/oidc/login?isSimplifiedFlow=true&email=${email}`), showDefaultMessage
    //     );
    // },
    getOktaMappingForTenant(){
        return makeCancellableAPIRequest(
            'GET',
            '/api/okta/oktaMappingForTenant'
        )
    },
    syncOktaUsers(){
        return makeCancellableAPIRequest(
            'POST',
            '/api/okta/usersync'
        )
    },
    getLoginAttempts(data) {
        let params = {
            ...data.filters,
            ..._.omit(data, ['filters', 'sortColumn', 'sortOrder'])
        }

        if(params.createdAt){
            let startDate, endDate;
            if(params.createdAt.length === 1) {
                [startDate, endDate] = getStartAndEndDateForQuickOption({
                  label: params.createdAt[0],
                }).map(m => m.toDate().toUTCString());
            } else {
                startDate = moment(params.createdAt[0]).toDate().toUTCString();
                endDate = moment(params.createdAt[1]).toDate().toUTCString();
            }
            params = {
                startDate,
                endDate,
                ..._.omit(params, ['createdAt'])
            }
        }

        return makeCancellableAPIRequest(
            'GET',
            '/api/failed-login/attempts',
            {
                params
            }
        )
    },
    getLoginAttemptsFilter() {
        return makeCancellableAPIRequest(
            'GET',
            '/api/failed-login/filters'
          )
    },
    updateLoginAttempts(id, params) {
        return makeCancellableAPIRequest(
            'PUT',
            `/api/failed-login/attempts/${id}`,
            {
                params
            }
        )
    },
    getTeamLeads(params) {
        return makeCancellableAPIRequest(
            'GET',
            '/user/team-leads',
            {
                params
            }
        )
    },
    getTeams() {
        return makeCancellableAPIRequest(
            'GET',
            '/user/get-teams'
        )
    },
    setPrivacyStatus(bool) {
        return makeCancellableAPIRequest(
            'POST',
            `/user/accept-reject?action=${bool}`
        )
    },
    getPublicUser(uuid) {
        return makeCancellableAPIRequest(
            'GET',
            `/public/accessrequest/email?uuid=${uuid}`,
            {},
            false
        )
    },
    getTenants(params) {
        return makeCancellableAPIRequest(
            'GET',
            '/user/tenants',
            { params }
        )
    },
    getFeatureFlags() {
        return makeCancellableAPIRequest(
            'GET',
            '/user/feature-flag'
        )
    },
    deleteRole(roleId){
        return makeCancellableAPIRequest(
            'DELETE',
            `/admin/user/role/?id=${roleId}`
        )
    },
    updateUserPreference(data){
        return makeCancellableAPIRequest(
            'PUT',
            '/api/v2/user/preference',
            { data }
        )
    },
    usersByAccessibleTeams(data){
        return makeCancellableAPIRequest(
            'POST',
            '/api/v2/user/accessible-teams',
            { data }
        )
    }
}

export default UserAPI;